import {
  Ingredient, Transformation, Tool, createIngredient, createTransformation, createTool,
  updateIngredient, updateTransformation, updateTool,
  addIngredientImage, deleteIngredientImage, setDefaultIngredientImage,
  addTransformationImage, deleteTransformationImage, setDefaultTransformationImage,
  addToolImage, deleteToolImage, setDefaultToolImage,
  listTags, createOrGetTag, addTagToIngredient, removeTagFromIngredient,
} from '../../api/index.js';
import { Tag } from '../../api/types.js';
import { ImageUploader } from './ImageUploader.js';
import { ImagePool, ImagePoolItem } from './ImagePool.js';
import { TagPicker } from './TagPicker.js';
import { setIcon } from '../../ui/icon.js';
import { renderDurationInput } from './DurationInput.js';

export type EntityType = 'ingredient' | 'transformation' | 'tool';
export type EntityData = Ingredient | Transformation | Tool;

interface InlineEntityEditorOptions {
  entityType: EntityType;
  /** When present, edits the existing entity. When absent, creates a new one. */
  existingEntity?: EntityData;
  container: HTMLElement;
  /** Pre-filled name (for create) or taken from existingEntity (for edit). */
  initialName?: string;
  onCreated?: (entity: EntityData) => void;
  onUpdated?: (entity: EntityData) => void;
  onCancelled?: () => void;
  /** Edit mode only: fired immediately whenever the entity is mutated in
   *  place outside the Save button flow — an image pool change (add/
   *  delete/set-default) or, for ingredients, a tag add/remove — since
   *  both take effect right away rather than waiting for Save. */
  onImagesChanged?: (entity: EntityData) => void;
  /** When provided, the form's own Guarda/Cancel·la button row is not
   *  rendered — fired once (at render time) with functions that trigger
   *  the exact same save/cancel behavior, so a caller can drive them from
   *  buttons of its own placed elsewhere (e.g. ExplorerPage puts them next
   *  to the row being edited, not below the form). */
  onActionsReady?: (actions: { save: () => void; cancel: () => void }) => void;
}

/**
 * Inline entity editor — same form for both create and edit.
 * The only difference is the submit button label ("Create" vs "Save").
 */
export class InlineEntityEditor {
  // Same 26px circled-button look as the entity card's own ✎/× header
  // (NodeVisualizer.renderEntityCard()) — kept here as one constant so the
  // two headers can't silently drift apart.
  private static readonly HEADER_BTN_STYLE = `
    width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
    border: 1px solid var(--accent-alpha-40); background: transparent; color: var(--accent);
    font-size: 13px; font-weight: bold; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.15s ease;
  `;

  private entityType: EntityType;
  private container: HTMLElement;
  private existingEntity: EntityData | undefined;
  private initialName: string;
  private onCreated?: (entity: EntityData) => void;
  private onUpdated?: (entity: EntityData) => void;
  private onCancelled?: () => void;
  private onImagesChanged?: (entity: EntityData) => void;
  private onActionsReady?: (actions: { save: () => void; cancel: () => void }) => void;
  private imageUploader: ImageUploader | null = null;
  private imagePool: ImagePool | null = null;
  private selectedFile: Blob | null = null;
  private idPrefix: string;
  private tagPicker: TagPicker | null = null;
  /** Create mode only (entityType === 'ingredient', no existingEntity):
   *  tags picked before the ingredient itself exists — see renderTagsSection(). */
  private pendingTags: Tag[] = [];
  /** Tools only — seed a new usage's own hidden/counts when this tool is
   *  first attached to a node (see recipe_model.ToolBase's doc comment).
   *  Initialized from the existing entity (edit mode) or the defaults
   *  below (create mode), mutated in place by the toggle buttons. */
  private toolDefaultHidden = false;
  private toolDefaultCounts = true;
  /** Transformations only — seconds. Seeds a new usage's own `duration`
   *  when this transformation is first attached (see
   *  recipe_model.TransformationBase.typical_duration's doc comment). */
  private transformationTypicalDuration: number | undefined;

  constructor(options: InlineEntityEditorOptions) {
    this.entityType = options.entityType;
    this.container = options.container;
    this.existingEntity = options.existingEntity;
    this.idPrefix = `inline-${this.entityType}-${Date.now()}`;
    this.initialName = options.initialName ?? this.existingEntity?.name ?? '';
    this.onCreated = options.onCreated;
    this.onUpdated = options.onUpdated;
    this.onCancelled = options.onCancelled;
    this.onImagesChanged = options.onImagesChanged;
    this.onActionsReady = options.onActionsReady;
    if (this.entityType === 'tool') {
      const existing = this.existingEntity as (Tool | undefined);
      this.toolDefaultHidden = existing?.defaultHidden ?? false;
      this.toolDefaultCounts = existing?.defaultCounts ?? true;
    }
    if (this.entityType === 'transformation') {
      this.transformationTypicalDuration = (this.existingEntity as (Transformation | undefined))?.typicalDuration;
    }
  }

  /**
   * Render the inline form into the container.
   */
  render(): void {
    this.container.innerHTML = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'border border-[var(--accent-alpha-20)] rounded-md overflow-hidden bg-[#0d1117]';

    // ── Header band: ✓ save / ✕ cancel, circled — same look (and same
    // spot) as the entity card's own ✎/× header (NodeVisualizer's
    // renderEntityCard()), so switching from viewing to editing a card
    // doesn't change where its action buttons live. When the caller drives
    // save/cancel from buttons of its own (onActionsReady), these two are
    // still created (handleSubmit()/onCancelled need something to attach
    // to) but stay detached — driven only via the `.click()` proxies handed
    // back below, same as before this header existed.
    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.title = this.existingEntity ? 'Guarda' : 'Crea';
    saveBtn.textContent = '✓';
    saveBtn.style.cssText = InlineEntityEditor.HEADER_BTN_STYLE;

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.title = 'Cancel·la';
    cancelBtn.textContent = '✕';
    cancelBtn.style.cssText = InlineEntityEditor.HEADER_BTN_STYLE;

    if (!this.onActionsReady) {
      const header = document.createElement('div');
      header.style.cssText = `
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 4px;
        padding: 4px 6px;
        background: var(--accent-alpha-13);
      `;
      header.appendChild(saveBtn);
      header.appendChild(cancelBtn);
      wrapper.appendChild(header);
    }

    const body = document.createElement('div');
    body.className = 'p-3 space-y-3';
    wrapper.appendChild(body);

    // ── Image section (centered, at top) ──
    const imageContainer = document.createElement('div');
    imageContainer.id = `${this.idPrefix}-image-container`;
    imageContainer.className = 'flex flex-col items-center space-y-2';
    body.appendChild(imageContainer);

    this.renderImageSection(imageContainer);

    // ── Name field ──
    const nameGroup = document.createElement('div');
    nameGroup.className = 'space-y-1';

    const nameLabel = document.createElement('label');
    nameLabel.className = 'block text-xs font-medium text-[#a0a0a0] font-mono uppercase tracking-wider';
    nameLabel.textContent = 'Nom';
    nameGroup.appendChild(nameLabel);

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.className = 'w-full px-3 py-2 text-sm border border-[var(--accent-alpha-20)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--accent)] bg-[#0d1117] text-[#e0e0e0] font-mono';
    nameInput.placeholder = this.entityType === 'ingredient' ? 'p. ex., Tomàquets'
      : this.entityType === 'tool' ? 'p. ex., Olla' : 'p. ex., Tallar';
    nameInput.value = this.initialName;
    nameGroup.appendChild(nameInput);
    body.appendChild(nameGroup);

    // ── Description field ──
    const descGroup = document.createElement('div');
    descGroup.className = 'space-y-1';

    const descLabel = document.createElement('label');
    descLabel.className = 'block text-xs font-medium text-[#a0a0a0] font-mono uppercase tracking-wider';
    descLabel.textContent = 'Descripció';
    descGroup.appendChild(descLabel);

    const descTextarea = document.createElement('textarea');
    descTextarea.className = 'w-full px-3 py-2 text-sm border border-[var(--accent-alpha-20)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--accent)] bg-[#0d1117] text-[#e0e0e0] font-mono';
    descTextarea.rows = 2;
    descTextarea.placeholder = 'Descripció opcional...';
    descTextarea.value = this.existingEntity?.description ?? '';
    descGroup.appendChild(descTextarea);
    body.appendChild(descGroup);

    // ── Default hidden/counts toggles (tools only) — no label, since the
    // buttons' own icon/glyph + tooltip carry the meaning, same convention
    // as the per-usage toggles in NodeVisualizer.renderToolUsageRows(). ──
    if (this.entityType === 'tool') {
      const defaultsGroup = document.createElement('div');
      defaultsGroup.className = 'flex items-center gap-2';

      const hiddenBtn = document.createElement('button');
      hiddenBtn.type = 'button';
      const applyHiddenState = () => {
        setIcon(hiddenBtn, this.toolDefaultHidden ? '/static/img/eye-closed.svg' : '/static/img/eye.svg', '13px');
        hiddenBtn.title = this.toolDefaultHidden
          ? 'Per defecte amagat de l\'elaboració — fes clic per canviar-ho'
          : 'Per defecte visible a l\'elaboració — fes clic per canviar-ho';
        hiddenBtn.style.cssText = this.toolDefaultHidden
          ? `
            width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
            border: 1px solid var(--accent-alpha-40); background: var(--accent); color: #0d1117;
            font-size: 13px; cursor: pointer;
            display: flex; align-items: center; justify-content: center;
            transition: all 0.15s ease;
          `
          : `
            width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
            border: 1px solid var(--accent-alpha-40); background: transparent; color: var(--accent);
            font-size: 13px; cursor: pointer;
            display: flex; align-items: center; justify-content: center;
            transition: all 0.15s ease;
          `;
      };
      applyHiddenState();
      hiddenBtn.addEventListener('click', () => {
        this.toolDefaultHidden = !this.toolDefaultHidden;
        applyHiddenState();
      });
      defaultsGroup.appendChild(hiddenBtn);

      const countsBtn = document.createElement('button');
      countsBtn.type = 'button';
      countsBtn.textContent = '#';
      const applyCountsState = () => {
        countsBtn.title = this.toolDefaultCounts
          ? 'Es compta per defecte — fes clic per canviar-ho'
          : 'No es compta per defecte — fes clic per canviar-ho';
        countsBtn.style.cssText = !this.toolDefaultCounts
          ? `
            width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
            border: 1px solid var(--accent-alpha-40); background: var(--accent); color: #0d1117;
            font-size: 13px; font-weight: bold; cursor: pointer;
            display: flex; align-items: center; justify-content: center;
            transition: all 0.15s ease;
          `
          : `
            width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
            border: 1px solid var(--accent-alpha-40); background: transparent; color: var(--accent);
            font-size: 13px; font-weight: bold; cursor: pointer;
            display: flex; align-items: center; justify-content: center;
            transition: all 0.15s ease;
          `;
      };
      applyCountsState();
      countsBtn.addEventListener('click', () => {
        this.toolDefaultCounts = !this.toolDefaultCounts;
        applyCountsState();
      });
      defaultsGroup.appendChild(countsBtn);

      body.appendChild(defaultsGroup);
    }

    // ── Tags (ingredients only — TransformationBase has no equivalent) ──
    if (this.entityType === 'ingredient') {
      const tagsGroup = document.createElement('div');
      tagsGroup.className = 'space-y-1';

      const tagsLabel = document.createElement('label');
      tagsLabel.className = 'block text-xs font-medium text-[#a0a0a0] font-mono uppercase tracking-wider';
      tagsLabel.textContent = 'Etiquetes';
      tagsGroup.appendChild(tagsLabel);

      const tagsContainer = document.createElement('div');
      tagsContainer.id = `${this.idPrefix}-tags-container`;
      tagsGroup.appendChild(tagsContainer);
      body.appendChild(tagsGroup);

      this.renderTagsSection(tagsContainer);
    }

    // ── Render template field (only for transformations) ──
    if (this.entityType === 'transformation') {
      const tmplGroup = document.createElement('div');
      tmplGroup.className = 'space-y-1';

      const tmplLabel = document.createElement('label');
      tmplLabel.className = 'block text-xs font-medium text-[#a0a0a0] font-mono uppercase tracking-wider';
      tmplLabel.textContent = 'Plantilla de preparació';
      tmplGroup.appendChild(tmplLabel);

      const tmplTextarea = document.createElement('textarea');
      tmplTextarea.className = 'w-full px-3 py-2 text-sm border border-[var(--accent-alpha-20)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--accent)] bg-[#0d1117] text-[#e0e0e0] font-mono';
      tmplTextarea.rows = 3;
      tmplTextarea.placeholder = '{self.name}: Posa {joined_names(self.children)} al forn a {params.degrees}°C';
      tmplTextarea.value = (this.existingEntity as any)?.renderTemplate ?? '';
      tmplGroup.appendChild(tmplTextarea);

      // Insert-at-cursor palette of every variable/function the template
      // engine understands (see template-engine.ts) — clicking a row
      // inserts its text at the textarea's current cursor position rather
      // than requiring it to be typed/remembered. Function calls (always
      // shown with a trailing "()") land the cursor just inside the
      // parens, ready for an argument; plain variables land right after
      // the inserted text.
      const paletteBox = document.createElement('div');
      paletteBox.className = 'border border-[var(--accent-alpha-13)] rounded-md';
      paletteBox.style.cssText = 'max-height: 110px; overflow-y: auto;';

      const insertAtCursor = (text: string) => {
        const start = tmplTextarea.selectionStart ?? tmplTextarea.value.length;
        const end = tmplTextarea.selectionEnd ?? tmplTextarea.value.length;
        tmplTextarea.value = tmplTextarea.value.slice(0, start) + text + tmplTextarea.value.slice(end);
        const parenIdx = text.indexOf('(');
        const cursorPos = start + (parenIdx === -1 ? text.length : parenIdx + 1);
        tmplTextarea.focus();
        tmplTextarea.setSelectionRange(cursorPos, cursorPos);
      };

      // Every header sticks at the same top:0 — rather than each at its
      // own offset (which would keep them all visible, stacked), this is
      // what makes a later header slide up and cover the previous one
      // once scrolled far enough past it (standard sticky-header-per-
      // section behavior), instead of the two ever being stacked
      // simultaneously.
      const addSection = (sectionLabel: string, items: string[]) => {
        const sectionHeader = document.createElement('div');
        sectionHeader.className = 'h-5 leading-5 px-2 text-xs text-[#a0a0a0] font-mono uppercase tracking-wider bg-[#1e1e2e] sticky top-0 z-10';
        sectionHeader.textContent = sectionLabel;
        paletteBox.appendChild(sectionHeader);

        for (const item of items) {
          const row = document.createElement('button');
          row.type = 'button';
          row.textContent = item;
          row.className = 'block w-full text-left px-2 py-0.5 text-sm font-mono text-[var(--accent)] bg-transparent hover:bg-[var(--accent-alpha-13)] transition-colors';
          row.addEventListener('click', () => insertAtCursor(item));
          paletteBox.appendChild(row);
        }
      };

      addSection('Variables', [
        'self.name', 'self.id', 'self.description', 'self.children',
        'self.parents', 'self.quantityPrefix', 'self.kind', 'params.', 'duration',
      ]);
      addSection('Funcions', [
        'graph.node()', 'joined_names()', 'without()', 'only()', 'tools()',
        'format_quantity()', 'format_duration()', 'capitalize()', 'lower()', 'upper()',
      ]);

      tmplGroup.appendChild(paletteBox);

      body.appendChild(tmplGroup);

      // Store reference for use in handleSubmit
      (this as any)._renderTemplateTextarea = tmplTextarea;

      // ── Typical duration — seeds a new usage's own duration (see
      // recipe_model.TransformationBase.typical_duration's doc comment). ──
      const durationGroup = document.createElement('div');
      durationGroup.className = 'space-y-1';
      const durationLabel = document.createElement('label');
      durationLabel.className = 'block text-xs font-medium text-[#a0a0a0] font-mono uppercase tracking-wider';
      durationLabel.textContent = 'Durada típica';
      durationGroup.appendChild(durationLabel);

      renderDurationInput(
        durationGroup,
        this.transformationTypicalDuration,
        (seconds) => { this.transformationTypicalDuration = seconds; },
        {
          inputClassName: 'w-16 flex-shrink-0 px-3 py-2 text-sm border border-[var(--accent-alpha-20)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--accent)] bg-[#0d1117] text-[#e0e0e0] font-mono',
          labelClassName: 'text-sm text-[#a0a0a0] font-mono',
        },
      );

      body.appendChild(durationGroup);
    }

    // ── Wire the save/cancel buttons created above ──
    saveBtn.addEventListener('click', async () => {
      await this.handleSubmit(nameInput.value, descTextarea.value, saveBtn);
    });
    cancelBtn.addEventListener('click', () => this.onCancelled?.());

    if (this.onActionsReady) {
      // Caller supplies its own buttons elsewhere and drives these two via
      // the functions handed back here — .click() still runs the exact
      // same handlers above even though the buttons themselves are never
      // attached to the DOM.
      this.onActionsReady({ save: () => saveBtn.click(), cancel: () => cancelBtn.click() });
    }

    this.container.appendChild(wrapper);

    // Focus name input
    nameInput.focus();
    if (this.initialName) nameInput.select();
  }

  /**
   * Tags section (ingredients only) — two modes, same split as the image
   * section above:
   * - Edit mode: the ingredient already exists, so add/remove takes
   *   effect immediately against it (no Save needed), matching the image
   *   pool's own behavior.
   * - Create mode: there's no id yet to assign a tag *to* — tags picked
   *   are held in `pendingTags` and applied right after the POST that
   *   creates the ingredient (handleSubmit()'s create branch), the same
   *   deferred-until-created pattern the image upload already uses.
   * Fetches the full tag list once for autocomplete — small, shared,
   * cacheable dataset, nothing like the per-ingredient image pool.
   */
  private renderTagsSection(container: HTMLElement): void {
    listTags().then((allTags) => {
      const existing = this.existingEntity as Ingredient | undefined;
      this.tagPicker = new TagPicker({
        container,
        idPrefix: `${this.idPrefix}-tags`,
        tags: existing ? (existing.tags ?? []) : this.pendingTags,
        allTags,
        addFn: existing
          ? async (name) => {
              const updated = await addTagToIngredient(existing.id, name);
              (existing as unknown as Record<string, unknown>).tags = updated.tags;
              this.onImagesChanged?.(existing); // reuses the same "entity mutated in place" notification images already fire
              const added = updated.tags?.find(t => t.name.toLowerCase() === name.toLowerCase());
              if (!added) throw new Error('Tag not found after adding');
              return added;
            }
          : async (name) => createOrGetTag(name),
        removeFn: existing
          ? async (tagId) => {
              const updated = await removeTagFromIngredient(existing.id, tagId);
              (existing as unknown as Record<string, unknown>).tags = updated.tags;
              this.onImagesChanged?.(existing);
            }
          : async () => { /* create mode: nothing assigned yet, just drop it locally below */ },
        onTagsChanged: (tags) => {
          if (!existing) this.pendingTags = tags;
        },
      });
      this.tagPicker.render();
    }).catch((error) => {
      console.error('Failed to load tags:', error);
    });
  }

  /**
   * Handle form submission. Creates a new entity or updates the existing one.
   */
  private async handleSubmit(
    name: string,
    description: string,
    submitBtn: HTMLButtonElement
  ): Promise<void> {
    const trimmedName = name.trim();
    const trimmedDesc = description.trim();

    if (!trimmedName) {
      this.imageUploader?.setError('El nom és obligatori');
      return;
    }

    this.imageUploader?.clearErrorExternal();
    submitBtn.disabled = true;
    submitBtn.style.opacity = '0.5';
    submitBtn.title = this.existingEntity ? 'Guardant...' : 'Creant...';

    try {
      // Get render template value if editing a transformation
      const renderTemplate = this.entityType === 'transformation'
        ? ((this as any)._renderTemplateTextarea?.value ?? '').trim() || undefined
        : undefined;

      if (this.existingEntity) {
        // ── Edit mode ──
        const updatePayload: Record<string, unknown> = {
          name: trimmedName,
          description: trimmedDesc || undefined,
        };
        if (renderTemplate !== undefined) {
          updatePayload.renderTemplate = renderTemplate;
        }
        if (this.entityType === 'transformation') {
          updatePayload.typicalDuration = this.transformationTypicalDuration ?? null;
        }

        const entity = this.entityType === 'ingredient'
          ? await updateIngredient(this.existingEntity.id, { name: trimmedName, description: trimmedDesc || undefined })
          : this.entityType === 'tool'
          ? await updateTool(this.existingEntity.id, {
              name: trimmedName, description: trimmedDesc || undefined,
              defaultHidden: this.toolDefaultHidden, defaultCounts: this.toolDefaultCounts,
            })
          : await updateTransformation(this.existingEntity.id, updatePayload as any);

        this.onUpdated?.(entity);
      } else {
        // ── Create mode ──
        const createPayload: Record<string, unknown> = {
          name: trimmedName,
          description: trimmedDesc || undefined,
        };
        if (renderTemplate !== undefined) {
          createPayload.renderTemplate = renderTemplate;
        }
        if (this.entityType === 'transformation') {
          createPayload.typicalDuration = this.transformationTypicalDuration ?? null;
        }

        const entity = this.entityType === 'ingredient'
          ? await createIngredient({ name: trimmedName, description: trimmedDesc || undefined })
          : this.entityType === 'tool'
          ? await createTool({
              name: trimmedName, description: trimmedDesc || undefined,
              defaultHidden: this.toolDefaultHidden, defaultCounts: this.toolDefaultCounts,
            })
          : await createTransformation(createPayload as any);

        let finalEntity: EntityData = entity;

        // Upload the picked image, if any, into the new entity's pool
        // (its first image, so it becomes the default automatically).
        if (this.selectedFile) {
          const added = this.entityType === 'ingredient'
            ? await addIngredientImage(entity.id, this.selectedFile)
            : this.entityType === 'tool'
            ? await addToolImage(entity.id, this.selectedFile)
            : await addTransformationImage(entity.id, this.selectedFile);
          finalEntity = { ...finalEntity, images: [added] };
        }

        // Apply any tags picked before the ingredient existed (see
        // renderTagsSection()'s doc comment) — there's finally an id to
        // assign each one to now.
        if (this.entityType === 'ingredient' && this.pendingTags.length > 0) {
          let updated: Ingredient | undefined;
          for (const tag of this.pendingTags) {
            updated = await addTagToIngredient(entity.id, tag.name);
          }
          if (updated) finalEntity = { ...finalEntity, tags: updated.tags };
        }

        this.onCreated?.(finalEntity);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save';
      this.imageUploader?.setError(message);
      submitBtn.disabled = false;
      submitBtn.style.opacity = '';
      submitBtn.title = this.existingEntity ? 'Guarda' : 'Crea';
    }
  }

  /**
   * Create (or re-create) the image section.
   * - Edit mode: the entity already exists, so it gets the full image-pool
   *   cycler (add/delete/set-default, each taking effect immediately).
   * - Create mode: there's no id yet to upload a pool image against, so a
   *   single image can be picked and is uploaded once the entity is created
   *   (unchanged from before — the full pool experience is available once
   *   the entity is edited afterwards).
   */
  private renderImageSection(container: HTMLElement): void {
    container.innerHTML = '';

    if (this.existingEntity) {
      this.renderImagePool(container, this.existingEntity);
      return;
    }

    this.imageUploader = new ImageUploader({
      container,
      idPrefix: `inline-${this.entityType}-${Date.now()}`,
      hideLabel: true,
      onImageSelected: (blob) => {
        this.selectedFile = blob;
        const previewUrl = URL.createObjectURL(blob);
        this.renderImageSectionPreview(container, previewUrl);
      },
    });
    this.imageUploader.render();
  }

  private renderImagePool(container: HTMLElement, entity: EntityData): void {
    const entityType = this.entityType;
    this.imagePool = new ImagePool({
      container,
      idPrefix: `${this.idPrefix}-pool`,
      images: entity.images ?? [],
      mode: 'manage',
      uploadFn: (blob) => entityType === 'ingredient'
        ? addIngredientImage(entity.id, blob)
        : entityType === 'tool'
        ? addToolImage(entity.id, blob)
        : addTransformationImage(entity.id, blob),
      deleteFn: (imageId) => entityType === 'ingredient'
        ? deleteIngredientImage(entity.id, imageId)
        : entityType === 'tool'
        ? deleteToolImage(entity.id, imageId)
        : deleteTransformationImage(entity.id, imageId),
      setDefaultFn: (imageId) => (entityType === 'ingredient'
        ? setDefaultIngredientImage(entity.id, imageId)
        : entityType === 'tool'
        ? setDefaultToolImage(entity.id, imageId)
        : setDefaultTransformationImage(entity.id, imageId)
      ).then(() => undefined),
      onPoolChanged: (images: ImagePoolItem[]) => {
        (entity as unknown as Record<string, unknown>).images = images;
        this.onImagesChanged?.(entity);
      },
    });
    this.imagePool.render();
  }

  /**
   * Re-render the image section with a temporary preview URL.
   * Used in create mode to show the cropped image before saving.
   */
  private renderImageSectionPreview(container: HTMLElement, previewUrl: string): void {
    container.innerHTML = '';

    this.imageUploader = new ImageUploader({
      container,
      idPrefix: `inline-${this.entityType}-${Date.now()}`,
      currentImageUrl: previewUrl,
      hideLabel: true,
      // No uploadEndpoint — we're in create mode, upload happens on submit
      onImageSelected: (blob) => {
        // Replace the previous selection
        this.selectedFile = blob;
        const newPreviewUrl = URL.createObjectURL(blob);
        URL.revokeObjectURL(previewUrl);
        this.renderImageSectionPreview(container, newPreviewUrl);
      },
      onImageRemoved: () => {
        this.selectedFile = null;
        URL.revokeObjectURL(previewUrl);
        this.renderImageSection(container);
      },
    });
    this.imageUploader.render();
  }
}
